<template>
	<header class="portal-header">
		<img class="portal-header__logo" :src="logoUrl" alt="Kova" />
		<PortalProfile class="portal-header__profile" tone="inverse" />
		<PortalIconButton
			ref="menuButton"
			class="portal-header__menu"
			label="Open menu"
			tone="dark"
			aria-controls="portal-menu"
			:aria-expanded="menuOpen"
			@click="emit('open-menu')"
		>
			<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
				<path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
			</svg>
		</PortalIconButton>
	</header>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import logoUrl from '@/assets/portal/kova-logo.svg'
import PortalIconButton from './PortalIconButton.vue'
import PortalProfile from './PortalProfile.vue'

/**
 * The dark header bar (Figma 13833:135699 desktop, 14254:175740 mobile): the logo, and on desktop the
 * signed-in customer; on a phone the menu button instead, which opens `PortalDrawer`. The menu glyph is
 * drawn here: the DS ships no burger icon (its LayoutScreen story says so on purpose), and the Figma
 * export of that layer is a "+" that does not match the frame's render.
 */

defineProps<{
	/** Mirrors the side menu for `aria-expanded`. */
	menuOpen: boolean
}>()

const emit = defineEmits<{ 'open-menu': [] }>()

const menuButton = ref<InstanceType<typeof PortalIconButton> | null>(null)

defineExpose({
	/** Where the focus returns when the side menu closes. */
	focusMenuButton: () => menuButton.value?.focus(),
})
</script>

<style scoped>
/* Figma 14254:175740: 68 tall — 14 above and below the 40px button — radius 20, 20 before the logo, 16 after the button. */
.portal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-16);
	padding: 14px var(--size-16) 14px var(--size-20);
	border-radius: var(--size-20);
	background: var(--background-brand-high);
}

.portal-header__logo {
	display: block;
	flex: none;
	height: var(--size-20);
	width: auto;
}

.portal-header__profile {
	display: none;
}

/* Figma 13833:135699: 32 above and below, the logo 28 tall, radius 32; the sides line up with the content gutter. */
@container portal (min-width: 1024px) {
	.portal-header {
		padding: var(--size-32) calc(var(--portal-gutter) - var(--portal-header-inset));
		border-radius: var(--size-32);
	}

	.portal-header__logo {
		height: var(--size-28);
	}

	.portal-header__profile {
		display: flex;
	}

	.portal-header__menu {
		display: none;
	}
}
</style>
